import { useState, useEffect } from 'react'
import { ROOM_PRESETS } from '../constants/room'

export default function RoomSettingsModal({ isOpen, onClose, room, onRoomChange, isFirstTime = false }) {
    const [tempRoom, setTempRoom] = useState(room)
    const [editingValue, setEditingValue] = useState(null)

    useEffect(() => {
        if (isOpen) {
            setTempRoom(room)
        }
    }, [isOpen, room])

    if (!isOpen) return null

    const handlePresetChange = (preset) => {
        setTempRoom({ width: preset.width, depth: preset.depth, height: preset.height })
    }

    const handleDimensionChange = (dimension, value) => {
        setTempRoom({ ...tempRoom, [dimension]: Number(value) })
    }

    const handleApply = () => {
        onRoomChange(tempRoom)
        onClose()
    }

    const handleValueClick = (field, currentValue) => {
        setEditingValue({ field, value: currentValue })
    }

    const handleValueChange = (e) => {
        const value = e.target.value
        if (value === '' || /^\d+$/.test(value)) {
            setEditingValue({ ...editingValue, value })
        }
    }

    const handleValueBlur = () => {
        if (editingValue && editingValue.value !== '') {
            const numValue = Number(editingValue.value)
            let clampedValue = numValue
            if (editingValue.field === 'width' || editingValue.field === 'depth') {
                clampedValue = Math.max(200, Math.min(800, numValue))
            } else if (editingValue.field === 'height') {
                clampedValue = Math.max(200, Math.min(400, numValue))
            }
            handleDimensionChange(editingValue.field, clampedValue)
        }
        setEditingValue(null)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleValueBlur()
        } else if (e.key === 'Escape') {
            setEditingValue(null)
        }
    }

    const renderValue = (field, value) => {
        const isEditing = editingValue?.field === field

        if (isEditing) {
            return (
                <input
                    type="text"
                    value={editingValue.value}
                    onChange={handleValueChange}
                    onBlur={handleValueBlur}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    className="w-20 px-2 py-0 text-sm text-primary-400 bg-white/10 border border-primary-400 rounded text-right"
                />
            )
        }

        return (
            <span
                onClick={() => handleValueClick(field, value)}
                className="text-sm font-semibold text-primary-400 cursor-pointer hover:text-primary-300 hover:underline"
            >
                {value} cm
            </span>
        )
    }

    // 現在選択されているプリセットを判定
    const getSelectedPreset = () => {
        const match = ROOM_PRESETS.find(p => p.width === tempRoom.width && p.depth === tempRoom.depth)
        return match ? match.id : 'custom'
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="glass-effect rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border border-white/20">
                {/* ヘッダー */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            🏠 {isFirstTime ? '部屋のサイズを設定' : '部屋の設定'}
                        </h2>
                        <p className="text-xs text-gray-400 mt-1">
                            {isFirstTime ? 'まず部屋のサイズを決めてください' : '部屋のサイズを変更できます'}
                        </p>
                    </div>
                    {!isFirstTime && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-white text-2xl transition-colors"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* プリセット選択 */}
                <div className="mb-6">
                    <label className="block text-sm text-gray-300 mb-2">プリセット</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {ROOM_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                onClick={() => handlePresetChange(preset)}
                                className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${getSelectedPreset() === preset.id
                                    ? 'bg-primary-500 text-white shadow-lg'
                                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                                    }`}
                            >
                                {preset.name}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 寸法調整 */}
                <div className="space-y-4 mb-6">
                    {/* 幅 */}
                    <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-400 w-12">幅</label>
                        <input
                            type="range"
                            min="200"
                            max="800"
                            value={tempRoom.width}
                            onChange={(e) => handleDimensionChange('width', e.target.value)}
                            className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary-500"
                        />
                        {renderValue('width', tempRoom.width)}
                    </div>

                    {/* 奥行き */}
                    <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-400 w-12">奥行</label>
                        <input
                            type="range"
                            min="200"
                            max="800"
                            value={tempRoom.depth}
                            onChange={(e) => handleDimensionChange('depth', e.target.value)}
                            className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary-500"
                        />
                        {renderValue('depth', tempRoom.depth)}
                    </div>

                    {/* 高さ */}
                    <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-400 w-12">高さ</label>
                        <input
                            type="range"
                            min="200"
                            max="400"
                            value={tempRoom.height}
                            onChange={(e) => handleDimensionChange('height', e.target.value)}
                            className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary-500"
                        />
                        {renderValue('height', tempRoom.height)}
                    </div>
                </div>

                {/* ボタン */}
                <div className="flex gap-3">
                    {!isFirstTime && (
                        <button
                            onClick={onClose}
                            className="flex-1 py-3 px-4 bg-white/5 hover:bg-white/10 text-gray-300 font-medium rounded-xl transition-all"
                        >
                            キャンセル
                        </button>
                    )}
                    <button
                        onClick={handleApply}
                        className="flex-1 py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-all glow-effect"
                    >
                        {isFirstTime ? '🚀 開始する' : '適用'}
                    </button>
                </div>
            </div>
        </div>
    )
}
